import React from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

import Index from '../../pages/Index'
import BookDemo from '../../pages/BookDemo'
import SeoLanding from '../../pages/SeoLanding'
import SignupRoutes from '../signup/SignupRoutes'

/**
 * BoundaryAnimatedShell — owns the route transitions at the *boundaries*
 * of the public funnel:
 *
 *     landing (/)  ⇄  signup (/signup/*)  →  welcome (/community/welcome)
 *
 * Within-funnel transitions (/signup → /signup/account, between wizard
 * steps, etc.) are still owned by the inner AnimatePresence inside
 * SignupRoutes — those use direction-aware slide+fade.
 *
 * This shell's only job is the cross-group hand-off:
 *
 *   - Landing → Signup: landing fades out 200ms, persona screen fades
 *     in + translateY -8px → 0 over 350ms.
 *   - Signup → Welcome: wizard fades out 250ms, welcome page fades
 *     in + translateY -8px → 0 over 400ms (the "reveal").
 *
 * Why the parent-route + null-child pattern?
 *   AnimatePresence needs the wrapping component to stay mounted across
 *   route changes so it can compare children lists. A flat `<Routes>`
 *   with separate sibling routes for /, /signup/*, /community/welcome
 *   would unmount/remount this shell on every boundary crossing,
 *   breaking the animation. Wrapping them as children of one parent
 *   route keeps the shell instance stable.
 *
 *   We deliberately don't use <Outlet /> because Outlet always reflects
 *   the *current* location — exiting children would re-render with the
 *   destination URL mid-animation. Instead we render an inner <Routes>
 *   with a captured `location` prop, the same pattern SignupRoutes uses.
 *
 * Group keying:
 *   The motion.div's key is the *group* (landing / signup / welcome),
 *   not the full pathname. So /signup → /signup/account doesn't trigger
 *   the boundary AnimatePresence (same key) — only crossing into a
 *   different group does.
 *
 * Per-group exit timing:
 *   Each motion.div's `exit` prop reflects the *group it lives in*.
 *   When that motion.div is later removed, AnimatePresence uses its
 *   last-rendered exit prop. This way the OUTGOING animation timing
 *   matches the spec for the page being left, without ever needing to
 *   know about the destination.
 */

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1]

const GROUP_VARIANTS = {
  landing: {
    initial: { opacity: 1 },
    animate: { opacity: 1 },
    exit: {
      opacity: 0,
      transition: { duration: 0.2, ease: EASE_OUT_EXPO },
    },
    transition: { duration: 0 },
  },
  signup: {
    initial: { opacity: 0, y: -8 },
    animate: { opacity: 1, y: 0 },
    exit: {
      opacity: 0,
      transition: { duration: 0.25, ease: EASE_OUT_EXPO },
    },
    transition: { duration: 0.35, ease: EASE_OUT_EXPO },
  },
  welcome: {
    initial: { opacity: 0, y: -8 },
    animate: { opacity: 1, y: 0 },
    exit: {
      opacity: 0,
      transition: { duration: 0.2, ease: EASE_OUT_EXPO },
    },
    transition: { duration: 0.4, ease: EASE_OUT_EXPO },
  },
}

function getGroupKey(pathname) {
  if (pathname === '/' || pathname === '/book-demo') return 'landing'
  if (pathname === '/signup' || pathname.startsWith('/signup/')) return 'signup'
  return 'landing'
}

export default function BoundaryAnimatedShell() {
  const location = useLocation()
  const reduceMotion = useReducedMotion()

  const groupKey = getGroupKey(location.pathname)
  const variant = GROUP_VARIANTS[groupKey]

  if (reduceMotion) {
    return <BoundaryRoutes location={location} />
  }

  const groupShellCls =
    groupKey === 'signup'
      ? 'min-h-screen min-h-[100svh] bg-[var(--signup-bg-base)]'
      : groupKey === 'welcome'
        ? 'min-h-screen min-h-[100svh]'
        : ''

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={groupKey}
        className={groupShellCls}
        initial={variant.initial}
        animate={variant.animate}
        exit={variant.exit}
        transition={variant.transition}
      >
        <BoundaryRoutes location={location} />
      </motion.div>
    </AnimatePresence>
  )
}

/* ─── Inner route table ───
   Receives a captured `location` so exiting AnimatePresence children
   keep showing their original URL while they animate out. */

function BoundaryRoutes({ location }) {
  return (
    <Routes location={location}>
      <Route index                  element={<Index />} />
      <Route path="book-demo"       element={<BookDemo />} />
      <Route path="sports/:sport"   element={<SeoLanding section="sports" />} />
      <Route path="solutions/:audience" element={<SeoLanding section="solutions" />} />
      <Route path="signup/*"        element={<SignupRoutes />} />
    </Routes>
  )
}
